/**
 * Guide-owned device reachability checker utility
 *
 * This module provides connection testing for the installation guide (/guide)
 * with Storyline-matching timeouts. It is separate from deviceCheck.ts to
 * preserve existing landing page behavior while matching Storyline's longer
 * timeout values.
 *
 * Timeout differences:
 * - deviceCheck.ts: 5s cloud, 3s local (for landing pages)
 * - guideDeviceCheck.ts: 30s cloud, 10s local (matches Storyline)
 */

import { DEVICE_URL_PATTERNS } from './constants';
import { logger } from './logger';

// Create component logger for GuideDeviceCheck
const guideDeviceCheckLogger = logger.withComponent('GuideDeviceCheck');

/**
 * Timeout constants matching Storyline's connection test behavior
 */
const GUIDE_TIMEOUTS = {
  CLOUD_TIMEOUT_MS: 30000, // 30 seconds - matches Storyline
  LOCAL_TIMEOUT_MS: 10000, // 10 seconds - matches Storyline
} as const;

/**
 * Device connection status types
 */
export type GuideDeviceStatus = 'cloud' | 'local' | 'unreachable';

/**
 * Result object returned by guide device reachability checks
 */
export interface GuideDeviceCheckResult {
  /** Status of the device connection */
  status: GuideDeviceStatus;
  /** URL to redirect to if device is reachable */
  url?: string;
  /** Error message if check failed */
  error?: string;
}

/**
 * Custom error class for guide device check failures
 */
export class GuideDeviceCheckError extends Error {
  constructor(
    message: string,
    public readonly serial: string,
    public readonly attemptedUrls: string[]
  ) {
    super(message);
    this.name = 'GuideDeviceCheckError';
  }
}

/**
 * Validates and sanitizes a device serial number
 * @param serial - Raw serial number input
 * @returns Sanitized serial number
 * @throws GuideDeviceCheckError if serial is invalid
 */
function validateAndSanitizeSerial(serial: string): string {
  if (!serial || typeof serial !== 'string') {
    throw new GuideDeviceCheckError('Serial number is required', '', []);
  }

  const sanitized = serial.trim().toLowerCase();
  
  if (sanitized.length === 0) {
    throw new GuideDeviceCheckError('Serial number cannot be empty', serial, []);
  }

  // Basic validation - alphanumeric characters and hyphens only
  if (!/^[a-z0-9-]+$/.test(sanitized)) {
    throw new GuideDeviceCheckError('Serial number contains invalid characters', serial, []);
  }

  return sanitized;
}

/**
 * Tests device reachability at a specific URL with timeout
 * @param url - URL to test for reachability
 * @param timeoutMs - Timeout in milliseconds
 * @returns Promise that resolves to true if device is reachable
 * @throws Never throws - returns false on any error
 */
async function testDeviceReachability(url: string, timeoutMs: number): Promise<boolean> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    await fetch(url, {
      method: 'GET',
      mode: 'no-cors',
      signal: controller.signal,
      cache: 'no-cache',
      redirect: 'follow',
      // Disable keepalive to avoid connection pooling issues
      keepalive: false,
    });
    
    // In no-cors mode, we can't read the response body or status
    // but if the fetch completes without error, the device is likely reachable
    return true;
  } catch {
    // Any error (network, timeout, abort, CORS) means unreachable
    // This is expected behavior in no-cors mode
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Checks if a Fusus CORE device is reachable via cloud or local network
 *
 * This function is specifically for the installation guide and uses longer
 * timeouts matching Storyline behavior (30s cloud, 10s local).
 *
 * Connection test order (route-aware):
 * - V2 route: mDNS only (no videolink.io)
 * - V1 route: Cloud connection (HTTPS) with 30s timeout, then local fallback
 *
 * @param serial - Device serial number (will be sanitized)
 * @param isV2Route - Whether this is a V2 route (mDNS only if true)
 * @returns Promise resolving to device check result
 *
 * @example
 * ```typescript
 * const result = await checkFususDeviceForGuide('ABC123', true); // V2 route
 * if (result.status === 'cloud' || result.status === 'local') {
 *   window.location.href = result.url;
 * } else {
 *   showFailureLayer();
 * }
 * ```
 */
export async function checkFususDeviceForGuide(
  serial: string,
  isV2Route: boolean = false
): Promise<GuideDeviceCheckResult> {
  try {
    const sanitizedSerial = validateAndSanitizeSerial(serial);

    guideDeviceCheckLogger.info('Starting connection test for guide', {
      serial: sanitizedSerial,
      route: isV2Route ? '/v2' : '/v1',
    });

    // ============================================================
    // Route-aware device checking:
    // - V2 route: mDNS only (no videolink.io)
    // - V1 route: videolink.io + mDNS fallback (standard flow)
    // ============================================================
    if (isV2Route) {
      // ============================================================
      // V2 ROUTE: mDNS ONLY (no videolink.io)
      // This path ensures no videolink.io for V2 client-side serial flow
      // ============================================================
      guideDeviceCheckLogger.info('V2 route: using mDNS only (no videolink.io)');

      const localUrl = DEVICE_URL_PATTERNS.LOCAL(sanitizedSerial);

      guideDeviceCheckLogger.debug('Testing mDNS connection', {
        url: localUrl,
        timeout: GUIDE_TIMEOUTS.LOCAL_TIMEOUT_MS
      });

      const localReachable = await testDeviceReachability(localUrl, GUIDE_TIMEOUTS.LOCAL_TIMEOUT_MS);

      if (localReachable) {
        guideDeviceCheckLogger.info('Device reachable via mDNS');
        return {
          status: 'local',
          url: DEVICE_URL_PATTERNS.LOCAL_REDIRECT(sanitizedSerial),
        };
      }

      guideDeviceCheckLogger.warn('Device unreachable via mDNS');
      return {
        status: 'unreachable',
        error: 'Device is not reachable via mDNS. Ensure device is on the same network.'
      };
    }

    // ============================================================
    // V1 ROUTE: videolink.io with mDNS fallback (UNCHANGED)
    // This is the existing production behavior for /v1 routes
    // ============================================================
    guideDeviceCheckLogger.info('V1 route: using videolink.io with mDNS fallback');

    // Generate URLs for testing
    const cloudUrl = DEVICE_URL_PATTERNS.CLOUD(sanitizedSerial);
    const localUrl = DEVICE_URL_PATTERNS.LOCAL(sanitizedSerial);

    // First, try cloud connection (preferred) with 30s timeout
    guideDeviceCheckLogger.debug('Testing cloud connection', { url: cloudUrl, timeout: GUIDE_TIMEOUTS.CLOUD_TIMEOUT_MS });
    const cloudReachable = await testDeviceReachability(cloudUrl, GUIDE_TIMEOUTS.CLOUD_TIMEOUT_MS);
    if (cloudReachable) {
      guideDeviceCheckLogger.info('Device reachable via cloud');
      return {
        status: 'cloud',
        url: DEVICE_URL_PATTERNS.CLOUD_REDIRECT(sanitizedSerial),
      };
    }

    // If cloud fails, try local connection with 10s timeout
    guideDeviceCheckLogger.debug('Cloud unreachable, testing local connection', { url: localUrl, timeout: GUIDE_TIMEOUTS.LOCAL_TIMEOUT_MS });
    const localReachable = await testDeviceReachability(localUrl, GUIDE_TIMEOUTS.LOCAL_TIMEOUT_MS);
    if (localReachable) {
      guideDeviceCheckLogger.info('Device reachable via local network');
      return {
        status: 'local',
        url: DEVICE_URL_PATTERNS.LOCAL_REDIRECT(sanitizedSerial),
      };
    }

    // Both connections failed
    guideDeviceCheckLogger.warn('Device unreachable via both cloud and local');
    return {
      status: 'unreachable',
      error: 'Device is not reachable via cloud or local network'
    };
  } catch (error) {
    if (error instanceof GuideDeviceCheckError) {
      return {
        status: 'unreachable',
        error: error.message,
      };
    }

    // Unexpected error - log for debugging but don't expose to user
    guideDeviceCheckLogger.warn('Guide device check failed with unexpected error', error instanceof Error ? error : new Error(String(error)));
    
    return { 
      status: 'unreachable',
      error: 'An unexpected error occurred during device check'
    };
  }
}

/**
 * Type guard to check if a guide device check result indicates success
 * @param result - Device check result
 * @returns True if device is reachable (cloud or local)
 */
export function isGuideDeviceReachable(result: GuideDeviceCheckResult): result is GuideDeviceCheckResult & { url: string } {
  return result.status !== 'unreachable' && !!result.url;
}
